import React, { useCallback, useEffect, useRef } from 'react';
import { point, distance as turfDistance } from '@turf/turf';

interface MapPolylineProps {
  map: google.maps.Map | null;
  marker1: google.maps.Marker;
  marker2: google.maps.Marker;
  editable?: boolean;
  onChange?: (updatedPath: { latLng1: google.maps.LatLng; latLng2: google.maps.LatLng }) => void;
  setSelectedPolyline?: (polyline: google.maps.Polyline, index: number) => void;
  index?: number;
}

const MapPolyline: React.FC<MapPolylineProps> = ({
  map,
  marker1,
  marker2,
  editable = false,
  onChange,
  setSelectedPolyline,
  index,
}) => {
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const labelMarkerRef = useRef<google.maps.Marker | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const labelOverlayRef = useRef<google.maps.OverlayView | null>(null);
  const marker1Ref = useRef<google.maps.Marker>(marker1);
  const marker2Ref = useRef<google.maps.Marker>(marker2);

  const draggingMarkerRef = useRef<'marker1' | 'marker2' | null>(null);
  const mouseoverRef = useRef<boolean>(false);

  // Distance threshold in meters to consider a marker for dragging
  const thresholdMeters = 10;

  const getLat = (latLng: any): number =>
    typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
  const getLng = (latLng: any): number =>
    typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;
  const toLatLng = (latLng: any): google.maps.LatLng =>
    new google.maps.LatLng(getLat(latLng), getLng(latLng));

  function createLabelOverlay(latLng1: any, latLng2: any) {
    if (!map) return;

    class LabelOverlay extends google.maps.OverlayView {
      div: HTMLDivElement | null = null;

      onAdd() {
        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.background = 'rgba(0, 0, 0, 0.6)';
        this.div.style.color = 'white';
        this.div.style.fontSize = '14px';
        this.div.style.fontWeight = 'bold';
        this.div.style.borderRadius = '4px';
        this.div.style.whiteSpace = 'nowrap';
        this.getPanes()?.floatPane.appendChild(this.div);
      }

      draw() {
        if (!this.div) return;
        const projection = this.getProjection();
        if (!projection) return;

        const p1 = projection.fromLatLngToDivPixel(toLatLng(latLng1));
        const p2 = projection.fromLatLngToDivPixel(toLatLng(latLng2));

        if (!p1 || !p2) return;

        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        this.div.style.left = `${midX}px`;
        this.div.style.top = `${midY}px`;
        this.div.style.transform = 'translate(-50%, -50%)';

        this.div.style.padding = '2px 5px';

        const measurement = turfDistance(
          point([getLng(latLng1), getLat(latLng1)]),
          point([getLng(latLng2), getLat(latLng2)]),
          { units: 'meters' }
        );

        this.div.innerText = `${measurement.toFixed(2)} M`;
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }
    }

    // Remove existing overlay if any
    if (labelOverlayRef.current) {
      labelOverlayRef.current.setMap(null);
    }

    const overlay = new LabelOverlay();
    overlay.setMap(map);
    labelOverlayRef.current = overlay;
  }

  function updateLabel(latLng1, latLng2) {
    createLabelOverlay(latLng1, latLng2);
  }

  function getLatLngFromProjection(): google.maps.LatLng {
    if (!map || !overlayRef.current) return null;

    const mapDiv = map.getDiv();
    const rect = mapDiv.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const projection = overlayRef.current.getProjection();
    if (!projection) return null;

    return projection.fromContainerPixelToLatLng(new google.maps.Point(x, y));
  }

  const handleOnChange = (updatedPath) => {
    if (!onChange || !updatedPath) return;
    const latLng1 = updatedPath.getAt(0);
    const latLng2 = updatedPath.getAt(1);

    onChange({ latLng1, latLng2 });
  };

  const onMousePositionChange = useCallback((newLatLng: google.maps.LatLng) => {
    if (draggingMarkerRef.current === 'marker1') {
      updateLabel(newLatLng, marker2Ref.current.position);
    } else if (draggingMarkerRef.current === 'marker2') {
      updateLabel(marker1Ref.current.position, newLatLng);
    }
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const mouseLatLng = getLatLngFromProjection();
      if (!mouseLatLng) return;

      if (mouseLatLng && onMousePositionChange) {
        onMousePositionChange(mouseLatLng);
      }
    },
    [map, onMousePositionChange]
  );

  const handleDragstart = useCallback(
    (event: MouseEvent) => {
      const clickLatLng = getLatLngFromProjection();
      if (!clickLatLng) return;

      const clickedPoint = point([clickLatLng.lng(), clickLatLng.lat()]);
      const marker1Point = point([
        getLng(marker1Ref.current.position),
        getLat(marker1Ref.current.position),
      ]);
      const marker2Point = point([
        getLng(marker2Ref.current.position),
        getLat(marker2Ref.current.position),
      ]);

      const distanceToMarker1 = turfDistance(clickedPoint, marker1Point, { units: 'meters' });
      const distanceToMarker2 = turfDistance(clickedPoint, marker2Point, { units: 'meters' });
      if (
        mouseoverRef.current &&
        (distanceToMarker1 < thresholdMeters || distanceToMarker2 < thresholdMeters)
      ) {
        if (distanceToMarker1 < distanceToMarker2) {
          draggingMarkerRef.current = 'marker1';
        } else {
          draggingMarkerRef.current = 'marker2';
        }

        const mapDiv = map.getDiv();
        mapDiv.addEventListener('mousemove', handleMouseMove);
      }
    },
    [map, marker1, marker2, handleMouseMove]
  );

  const handleDragend = useCallback(() => {
    if (!map) return;
    const mapDiv = map.getDiv();
    mapDiv.removeEventListener('mousemove', handleMouseMove);
  }, [map, handleMouseMove]);

  useEffect(() => {
    if (!map) return;

    const mapDiv = map.getDiv();
    if (!mapDiv) return;

    mapDiv.addEventListener('mousedown', handleDragstart);
    mapDiv.addEventListener('mouseup', handleDragend);

    return () => {
      mapDiv.removeEventListener('mousedown', handleDragstart);
      mapDiv.removeEventListener('mouseup', handleDragend);
      mapDiv.removeEventListener('mousemove', handleMouseMove);
    };
  }, [map, handleDragstart, handleDragend, handleMouseMove]);

  useEffect(() => {
    if (!map) return;

    const overlay = new google.maps.OverlayView();
    overlay.setMap(map);
    overlay.onRemove = () => {};
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!map || !polylineRef.current || !marker1 || !marker2) return;

    marker1Ref.current = marker1;
    marker2Ref.current = marker2;

    const latLng1 = marker1.position;
    const latLng2 = marker2.position;

    if (!latLng1 || !latLng2) return;

    const currentPath = polylineRef.current.getPath();
    const currentLatLng1 = currentPath.getAt(0);
    const currentLatLng2 = currentPath.getAt(1);

    const hasChanged =
      getLat(currentLatLng1) !== getLat(latLng1) ||
      getLng(currentLatLng1) !== getLng(latLng1) ||
      getLat(currentLatLng2) !== getLat(latLng2) ||
      getLng(currentLatLng2) !== getLng(latLng2);

    // If line changes due to marker changes (undo, redo) update polyline path, listeners and label
    if (hasChanged) {
      polylineRef.current.setPath([
        new google.maps.LatLng(getLat(latLng1), getLng(latLng1)),
        new google.maps.LatLng(getLat(latLng2), getLng(latLng2)),
      ]);

      google.maps.event.addListener(polylineRef.current.getPath(), 'set_at', () => {
        handleOnChange(polylineRef.current.getPath());
      });

      updateLabel(latLng1, latLng2);
    }
  }, [marker1.position, marker2.position]);

  useEffect(() => {
    if (polylineRef.current) {
      polylineRef.current.setEditable(editable);
    }
  }, [editable]);

  // Initialize polyline and label when component mounts
  useEffect(() => {
    if (!map || !marker1 || !marker2) return;

    const latLng1 = marker1.position;
    const latLng2 = marker2.position;

    const path: google.maps.LatLng[] = [
      new google.maps.LatLng(getLat(latLng1), getLng(latLng1)),
      new google.maps.LatLng(getLat(latLng2), getLng(latLng2)),
    ];

    // Draw the polyline
    const polyline: google.maps.Polyline = new google.maps.Polyline({
      path,
      strokeColor: 'red',
      strokeOpacity: 0.8,
      strokeWeight: 4,
      map,
      editable,
      suppressUndo: true,
    });

    polylineRef.current = polyline;

    // initial label render
    updateLabel(marker1.position, marker2.position);

    const polyPath = polyline.getPath();
    const listeners: google.maps.MapsEventListener[] = [
      google.maps.event.addListener(polyPath, 'set_at', () => {
        handleOnChange(polyPath);
      }),
      google.maps.event.addListener(polyline, 'click', () => {
        if (setSelectedPolyline) {
          setSelectedPolyline(polyline, index);
        }
      }),
      google.maps.event.addListener(polyline, 'mouseover', () => {
        mouseoverRef.current = true;
      }),
      google.maps.event.addListener(polyline, 'mouseout', () => {
        mouseoverRef.current = false;
      }),
    ];

    return () => {
      const mapDiv = map.getDiv();
      mapDiv.removeEventListener('mousedown', handleDragstart);
      mapDiv.removeEventListener('mouseup', handleDragend);
      mapDiv.removeEventListener('mousemove', handleMouseMove);
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      if (labelOverlayRef.current) {
        labelOverlayRef.current.setMap(null);
        labelOverlayRef.current = null;
      }
      polyline.setMap(null);
      if (labelMarkerRef.current) labelMarkerRef.current.setMap(null);
      listeners.forEach((listener) => google.maps.event.removeListener(listener));
    };
  }, []);

  return null;
};

export default MapPolyline;
